import { Controller } from 'stimulus';
import events from 'lib/events';
import {
  ezDebounce, getWindowWidth, addListenerMulti, removeListenerMulti, triggerCustomEvent,
} from 'lib/helpers';

export default class EzCarouselController extends Controller {
  static targets= [
    'innerWrap',
    'slide',
    'arrowLeft',
    'arrowRight',
  ]

  connect() {
    this.options = {
      transition: {
        speed: this.element.dataset.speed || 500,
        easing: '',
      },
      swipe: true,
      afterChangeSlide: function afterChangeSlide() {},
    };

    this.arrows = {
      left: this.arrowLeftTarget,
      right: this.arrowRightTarget,
    };

    // Go ahead and initialize the carousel!
    this.initCarousel();
  }

  initCarousel() {
    // Set some initial values
    this.innerWrapTarget.style.transition = `left ${this.options.transition.speed / 1000}s ${this.options.transition.easing}`;
    this.curPos = 0;
    this.totalSlides = this.slideTargets.length;
    this.isAnimating = false;

    // Set up responsive behaviors
    window.addEventListener('resize', ezDebounce(() => {
      this.setupResponsive();
    }), 500);
    this.setupResponsive();

    // Set main slider width
    this.innerWrapTarget.style.width = `${this.calculateSlidesWidth() + 20}px`;

    // Set up swiping behaviors
    if (this.options.swipe) {
      addListenerMulti(this.innerWrapTarget, 'mousedown touchstart', this.startSwipe);
    }
  }

  calculateSlidesWidth(slidesArr = this.slideTargets) {
    let totalWidth = 0;

    for (let slide = 0, totalSlides = slidesArr.length;
      slide < totalSlides; slide += 1) {
      const slideWidth = slidesArr[slide].getAttribute('data-thumb-width') || slidesArr[slide].offsetWidth;

      totalWidth += Math.ceil(parseInt(slideWidth, 10)) + 10;
    }

    return totalWidth;
  }

  sendRelatedTagEvent(event) {
    const targetElement = event.target.closest('.ez-tags-carousel__tag-link');
    if (!targetElement) return;

    triggerCustomEvent(events.site_header.related_tag_click, {
      tagClicked: targetElement.getAttribute('title'),
      tagSearched: document.querySelector('#search').getAttribute('value'),
    });
  }

  handleTagClick = (event) => {
    event.preventDefault();

    const searchedEventDetail = {
      term: event.target.textContent,
      searchRoute: document.querySelector('#search').getAttribute('data-search-route'),
      useSearchState: true,
    };

    if (event.metaKey && event.code && event.code === 'Enter') {
      searchedEventDetail.searchInNewTab = true;
    }

    triggerCustomEvent('Searched', searchedEventDetail);
  }

  arrowClick(event) {
    const dir = event.currentTarget.dataset.direction;

    if (!this.element.classList.contains('is-animating')) {
      if (this.getCurEdge(dir) < 0) {
        setTimeout(() => {
          this.move(dir);
        }, 20);
      }
    }
  }

  setupResponsive() {
    this.isMobile = false;

    this.numSlidesMove = 1; // offset for lowest res

    if (getWindowWidth() > 800) {
      this.numSlidesMove = 6; // offset for highest res
    } else {
      if (getWindowWidth() > 450) {
        this.numSlidesMove = 3; // offset for middle res
      }

      this.isMobile = true;
    }

    this.resetCarousel();
  }

  resetCarousel() {
    this.innerWrapTarget.style.left = '0px';
    this.curPos = 0;
    this.arrows.left.classList.remove('is-active');
    this.arrows.right.classList.add('is-active');
  }

  getOppositeDir(dir) {
    let oppositeDir;

    if (dir === 'left') {
      oppositeDir = 'right';
    } else {
      oppositeDir = 'left';
    }

    return oppositeDir;
  }

  getCurEdge(dir) {
    let curEdge;

    if (dir === 'left') {
      curEdge = this.innerWrapTarget.offsetLeft;
    } else {
      curEdge = this.element.offsetWidth - (this.innerWrapTarget.offsetLeft + this.innerWrapTarget.offsetWidth);
    }

    return curEdge;
  }

  move(dir) {
    const transitionAmt = this.calcTransition(dir);

    this.innerWrapTarget.style.left = `${this.getCurEdge('left') + transitionAmt}px`;

    this.element.classList.add('is-animating');

    if (this.getCurEdge(dir) < 0) {
      this.arrows[this.getOppositeDir(dir)].classList.add('is-active');
    }

    setTimeout(() => {
      this.element.classList.remove('is-animating');

      if (this.getCurEdge(dir) >= 0) {
        this.arrows[dir].classList.remove('is-active');
      }

      this.curPos = this.nextPos;
    }, this.options.transition.speed);

    this.options.afterChangeSlide(this);
  }

  calcTransition(dir) {
    const movingItems = this.getSlidesSubArray(dir);

    let transitionAmt = this.calculateSlidesWidth(movingItems);

    if (this.predictNextEdge(dir, transitionAmt) < 0) {
      if (dir === 'left') {
        transitionAmt = -this.getCurEdge(dir);
      } else {
        transitionAmt = this.getCurEdge(dir);
      }
    } else if (dir === 'right') {
      transitionAmt *= -1;
    }

    return transitionAmt;
  }

  predictNextEdge(dir, transitionAmt) {
    return Math.abs(this.getCurEdge(dir)) - transitionAmt;
  }

  getSlidesSubArray(dir) {
    let subArray;

    if (dir === 'left') {
      this.nextPos = this.curPos - this.numSlidesMove;

      subArray = this.slideTargets.slice(this.nextPos, (this.nextPos + this.numSlidesMove));
    } else {
      this.nextPos = this.curPos + this.numSlidesMove;

      subArray = this.slideTargets.slice(this.curPos, this.nextPos);
    }
    return subArray;
  }

  startSwipe = (e) => {
    let touch = e;

    this.getCurEdge('left');

    if (!this.isAnimating) {
      if (e.type === 'touchstart') {
        touch = e.targetTouches[0] || e.changedTouches[0];
      }

      this.startX = touch.pageX;
      this.startY = touch.pageY;

      addListenerMulti(this.innerWrapTarget, 'mousemove touchmove', this.swipeMove);
      addListenerMulti(document.body, 'mouseup touchend', this.swipeEnd);
    }
  }

  swipeMove = (e) => {
    let touch = e;

    if (e.type === 'touchmove') {
      touch = e.targetTouches[0] || e.changedTouches[0];
    }

    this.moveX = touch.pageX;
    this.moveY = touch.pageY;

    // for scrolling up and down
    if (Math.abs(this.moveX - this.startX) < 40) return;

    this.isAnimating = true;

    this.element.classList.add('is-animating');

    e.preventDefault();

    const dir = this.startX < this.moveX ? 'left' : 'right';

    this.move(dir);
  }

  swipeEnd = () => {
    if (Math.abs(this.moveX - this.startX) === 0) return;

    delete this.startX;
    delete this.startY;
    delete this.moveX;
    delete this.moveY;

    this.isAnimating = false;

    this.element.classList.remove('is-animating');

    removeListenerMulti(this.innerWrapTarget, 'mousemove touchmove', this.swipeMove);
    removeListenerMulti(document.body, 'mouseup touchend', this.swipeEnd);
  }
}
